import React, { useState } from "react";
import {
    Box,
    TextField,
    Button,
    Typography,
    MenuItem,
    Stack,
    Card,
    CardContent,
} from "@mui/material";
import GambaRates from "../data/GambaRates";
import RatesSimulator from "./RatesSimulator";

const sanitizeText = (text) => {
    // remove potential HTML/script tags and trim whitespace
    const sanitized = text.replace(/[<>]/g, "").trim();
    return sanitized;
};

const isValidImageUrl = (url) => {
    try {
        const parsed = new URL(url);
        return /\.(jpg|jpeg|png|gif|webp|avif)$/i.test(parsed.pathname);
    } catch {
        return false;
    }
};

const CustomPoolBuilder = () => {
    const [selectedType, setSelectedType] = useState("");
    const [customPools, setCustomPools] = useState({});
    const [customSpecials, setCustomSpecials] = useState([]);
    const [showSimulator, setShowSimulator] = useState(false);
    const [errors, setErrors] = useState({});

    const extractSpecialItems = (outcomes) => {
        const specials = [];
        outcomes.forEach((o, i) => {
            o.items.forEach((it) => {
                if (it.specific) {
                    specials.push({
                        index: i,
                        name: it.specific,
                        image: it.image || "",
                    });
                }
            });
        });
        return specials;
    };

    const handleSelectType = (type) => {
        const base = GambaRates[type];
        setSelectedType(type);
        setCustomPools(JSON.parse(JSON.stringify(base.pools)));
        setCustomSpecials(extractSpecialItems(base.outcomes));
        setShowSimulator(false);
        setErrors({});
    };

    const handleClearSimulator = () => {
        setShowSimulator(false);
    };

    const handlePoolChange = (poolKey, index, field, value) => {
        const sanitizedValue =
            field === "image"
                ? value.trim()
                : sanitizeText(value);

        setCustomPools((prev) => {
            const updated = { ...prev };
            updated[poolKey][index][field] = sanitizedValue;
            return updated;
        });

        if (field === "image") {
            setErrors((prev) => ({
                ...prev,
                [`${poolKey}-${index}-image`]: sanitizedValue && !isValidImageUrl(sanitizedValue)
                    ? "Invalid image URL"
                    : "",
            }));
        }
    };

    const handleSpecialChange = (index, field, value) => {
        const sanitizedValue =
            field === "image"
                ? value.trim()
                : sanitizeText(value);

        setCustomSpecials((prev) => {
            const updated = [...prev];
            updated[index][field] = sanitizedValue;
            return updated;
        });

        if (field === "image") {
            setErrors((prev) => ({
                ...prev,
                [`special-${index}-image`]: sanitizedValue && !isValidImageUrl(sanitizedValue)
                    ? "Invalid image URL"
                    : "",
            }));
        }
    };

    const handleSimulate = () => {
        // Check for any invalid URLs before running
        const invalidEntries = Object.values(errors).some((err) => err);
        if (invalidEntries) {
            alert("Please fix invalid image URLs before simulating.");
            return;
        }

        const base = JSON.parse(JSON.stringify(GambaRates[selectedType]));
        let specialIndex = 0;

        base.outcomes.forEach((o) => {
            o.items.forEach((it) => {
                if (it.specific) {
                    const custom = customSpecials[specialIndex];
                    it.specific = sanitizeText(custom.name);
                    it.image = isValidImageUrl(custom.image) ? custom.image : "";
                    specialIndex++;
                }
            });
        });

        setShowSimulator({
            ...base,
            pools: customPools,
            outcomes: base.outcomes,
        });
    };

    return (
        <Box sx={{ mt: 2 }}>
            <TextField
                select
                label="Select Capsule Type"
                value={selectedType}
                onChange={(e) => handleSelectType(e.target.value)}
                sx={{
                    width: 300,
                    mb: 3,
                    backgroundColor: (theme) => theme.palette.background.paper,
                }}
            >
                {Object.keys(GambaRates).map((key) => (
                    <MenuItem key={key} value={key}>
                        {GambaRates[key].name}
                    </MenuItem>
                ))}
            </TextField>

            {/* Pool editor */}
            {selectedType && (
                <Box>
                    {Object.entries(customPools).map(([poolKey, poolItems]) => (
                        <Card
                            key={poolKey}
                            sx={{
                                boxShadow: 0,
                                mb: 0,
                                backgroundColor: (theme) =>
                                    theme.palette.mode === "dark"
                                        ? "rgba(91, 160, 216, 0.1)" // soft blue tint in dark mode
                                        : "rgba(226, 241, 255, 0.7)", // soft blue in light mode
                            }}>
                            <CardContent>
                                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                    Regular Pool
                                </Typography>
                                {poolItems.map((item, index) => (
                                    <Stack
                                        key={index}
                                        direction="row"
                                        spacing={2}
                                        alignItems="center"
                                        sx={{ mb: 2 }}
                                    >
                                        <TextField
                                            label="Item Name"
                                            value={item.name}
                                            onChange={(e) =>
                                                handlePoolChange(poolKey, index, "name", e.target.value)
                                            }
                                            size="small"
                                            sx={{ width: "40%" }}
                                        />
                                        <TextField
                                            label="Image URL"
                                            value={item.image}
                                            onChange={(e) =>
                                                handlePoolChange(poolKey, index, "image", e.target.value)
                                            }
                                            size="small"
                                            sx={{ width: "50%" }}
                                            error={Boolean(
                                                errors[`${poolKey}-${index}-image`]
                                            )}
                                            helperText={errors[`${poolKey}-${index}-image`] || ""}
                                        />
                                    </Stack>
                                ))}
                            </CardContent>
                        </Card>
                    ))}

                    {/* Bonus Items Section */}
                    {customSpecials.length > 0 && (
                        <Card sx={{
                            boxShadow: 0,
                            mb: 0,
                            backgroundColor: (theme) =>
                                theme.palette.mode === "dark"
                                    ? "rgba(91, 160, 216, 0.1)" // soft blue tint in dark mode
                                    : "rgba(226, 241, 255, 0.7)", // soft blue in light mode
                        }}>
                            <CardContent>
                                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                    LE Item(s)
                                </Typography>
                                {customSpecials.map((it, index) => (
                                    <Stack
                                        key={index}
                                        direction="row"
                                        spacing={2}
                                        alignItems="center"
                                        sx={{ mb: 2 }}
                                    >
                                        <TextField
                                            label={`LE Item #${index + 1} Name`}
                                            value={it.name}
                                            onChange={(e) =>
                                                handleSpecialChange(index, "name", e.target.value)
                                            }
                                            size="small"
                                            sx={{ width: "40%" }}
                                        />
                                        <TextField
                                            label="Image URL"
                                            value={it.image}
                                            onChange={(e) =>
                                                handleSpecialChange(index, "image", e.target.value)
                                            }
                                            size="small"
                                            sx={{ width: "50%" }}
                                            error={Boolean(errors[`special-${index}-image`])}
                                            helperText={errors[`special-${index}-image`] || ""}
                                        />
                                    </Stack>
                                ))}
                            </CardContent>
                        </Card>
                    )}

                    <Box mb={2}>
                        <Button
                            variant="contained"
                            onClick={handleSimulate}
                            sx={{ m: 1 }}
                        >
                            Create Simulator
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleClearSimulator}
                            sx={{ m: 1 }}
                        >
                            Clear Simulator
                        </Button>
                    </Box>
                </Box>
            )}

            {/* Simulator Output */}
            {showSimulator && (
                <Box sx={{ mt: 4 }}>
                    <RatesSimulator
                        boxName={`Custom - ${showSimulator.name}`}
                        boxImage={showSimulator.image}
                        outcomes={showSimulator.outcomes}
                        pools={showSimulator.pools}
                    />
                </Box>
            )}
        </Box>
    );
};

export default CustomPoolBuilder;